/**
 * The Overview of one service on the Apps tab (design "Flow Project Redesign", Apps board): cards read from what the
 * project already knows, each with its own failure (a card that cannot load says so; the others still show):
 * How it runs (built from code by which builder, Docker run or Docker Compose, from the folder and
 * `.flow/service.json`: mode, start command, port; whether it is the project's main service, the one a shared link
 * opens), Performance (CPU and memory now), Suggestions, Technologies, Endpoints (the API tab's list for this
 * service), Configuration (variables, secrets, jobs, secrets still to set), Quality (what blocks Publish now) and, for
 * a Docker Compose app, the compose file's services. With vfs-service's builders of the service (`builders`), How it runs
 * names the builder in use ("Makefile · make dev", "Dockerfile", "Node.js · npm run dev") and is the picker of every way
 * the service can run (BuilderPicker, a custom command included), saved in its `.flow/service.json`.
 * A service run as a Docker Compose app has the same "Preview entry point" a pinned compose file has (RunnableDetail's
 * EntryPicker, from the runner's `composeEntry` on `info`), saved as `entry: { service, port }` in its
 * `.flow/service.json` (the runner reads it at the start); while it runs, "Restart to apply" (`onApplyEntry`).
 * How it runs also has the service's "Deploy this service" switch (DeploySwitch, in `.flow/project.json`).
 * `service` is the service's short name (`api`), `info` the runner's view of it (port, build error, stack label),
 * `runner` its entry in the project's services (state, CPU, memory), `names` the short names of all the services.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as vfsClient from '../../utils/vfsClient';
import { getAppConfig } from '../../services/AppConfigService';
import { getEndpoints } from '../../services/ApiTesterService';
import { isRunning, serviceState } from '../../services/SandboxService';
import useQualityGate, { blockedCount } from '../../hooks/useQualityGate';
import { gateRules } from '../home/ReadyToPublish';
import { builderOf, detectTechnologies, howItRuns, isMainService } from '../../utils/serviceStackModel';
import {
  builderSummary, currentBuilder, specEntryOf, specWithEntry,
} from '../../utils/builderModel';
import BuilderPicker from './BuilderPicker';
import ServiceAddresses from './ServiceAddresses';
import { EntryPicker } from './RunnableDetail';
import { serviceSuggestions } from '../../utils/serviceSuggestionsModel';
import DeploySwitch from './DeploySwitch';
import { sameService } from '../../utils/serviceTabsModel';
import { Card, plural, useLoaded } from './ServiceTabs/common';
import useServiceFacts from './ServiceTabs/useServiceFacts';
import { DeviceBuildsCard } from '../devicebuilds/DeviceBuildEntry';
import { isDeviceBuilders } from '../../utils/deviceApps';
import { ComposeCard, PerformanceCard, SuggestionsCard, TechnologiesCard } from './ServiceTabs/OverviewWidgets';

const MAX_ENDPOINTS = 5;

/** The lines of "How it runs" from a service.json text: only what the file says, in the order a person reads it. */
export function runLines(text, port = null) {
  let spec = null;
  try { spec = JSON.parse(text); } catch { spec = null; }
  const lines = [];
  if (spec && typeof spec === 'object') {
    lines.push(['Mode', spec.run || (spec.start ? 'process' : 'detected')]);
    if (spec.start) { lines.push(['Start', Array.isArray(spec.start) ? spec.start.join(' ') : String(spec.start)]); }
    if (spec.build) { lines.push(['Build', Array.isArray(spec.build) ? spec.build.join(' ') : String(spec.build)]); }
    if (spec.file || spec.compose) { lines.push(['Entry point', spec.file || spec.compose]); }
    const entry = specEntryOf(spec);
    if (entry) { lines.push(['Compose entry', entry.port ? `${entry.service} · ${entry.port}` : entry.service]); }
    if (spec.dockerfile) { lines.push(['Dockerfile', String(spec.dockerfile)]); }
    if (spec.image) { lines.push(['Image', String(spec.image)]); }
    if (spec.kind === 'worker') { lines.push(['Kind', 'Worker (no port)']); }
    if (Array.isArray(spec.ports) && spec.ports.length) {
      lines.push(['Other ports', spec.ports.map((p) => `${p.name}${p.port ? ` ${p.port}` : ''}${p.protocol && p.protocol !== 'http' ? ` (${p.protocol})` : ''}`).join(', ')]);
    }
    if (spec.watch !== undefined) { lines.push(['Hot reload', spec.watch ? 'on' : 'off']); }
  }
  if (port) { lines.push(['Port', String(port)]); }
  return lines;
}

export default function ServiceOverview({
  projectId, service, info = null, shows = () => true, onOpen = null, onTab = null, onOpenLogs = null, runner = null, names = [],
  defaultApp = null, builders = null, docker = null, readOnly = false, onBuilderSaved = null, offerRestart = false, onRestart = null,
  onApplyEntry = null, device = null,
}) {
  const facts = useServiceFacts(projectId, service);
  // The service.json as last written by the entry point picker (its file is not read again for that).
  const [entrySpec, setEntrySpec] = useState(null);
  const api = useLoaded(() => getEndpoints(projectId), [projectId]);
  const cfg = useLoaded(() => getAppConfig(projectId), [projectId]);
  const { gate, loading: gateLoading } = useQualityGate(projectId);
  // Only rules that ran say anything: before the first run there is no verdict, never "Nothing blocks Publish".
  const checked = gateRules(gate).some((rule) => rule.status === 'pass' || rule.status === 'fail');

  const endpoints = (api.value?.services || []).find((item) => sameService(item.service, service))?.endpoints || [];
  const config = (cfg.value?.services || []).find((item) => sameService(item.name, service)) || null;
  const blocked = blockedCount(gate);
  const port = info?.previewPort || facts.spec?.port || null;
  const spec = entrySpec || facts.spec;
  const lines = runLines(entrySpec ? JSON.stringify(entrySpec) : facts.specText, info?.previewPort);
  const runs = howItRuns({ spec, names: facts.names, files: facts.files, stackLabel: info?.stackLabel || null });
  // A Docker Compose app on its own (run: "compose", or its compose file detected as the app): its preview entry point.
  const composeApp = !facts.loading && Boolean(projectId) && runs.kind === 'compose' && (!spec || spec.run === 'compose');
  const saveEntry = async(entry) => {
    const next = specWithEntry(spec, entry, runs.file || null);
    await vfsClient.writeFile(projectId, `services/${service}/.flow/service.json`, `${JSON.stringify(next, null, 2)}\n`);
    setEntrySpec(next);
  };
  const builder = builders && !facts.loading ? builderSummary(currentBuilder(facts.spec, builders), builders) : null;
  const technologies = detectTechnologies({ names: facts.names, files: facts.files });
  const main = names.length > 1 && isMainService(names, service, defaultApp);
  const open = (tab) => (onOpen && shows(tab) ? () => onOpen(tab) : null);
  // A suggestion's button shows that tab in this page; Logs without its in-page tab opens the Logs view.
  const here = (tab) => (onTab && shows(tab) ? () => onTab(tab) : null);
  const actions = { logs: here('logs') || onOpenLogs, config: here('config'), quality: here('quality'), api: here('api') };
  const suggestions = facts.loading ? [] : serviceSuggestions({
    // A native device app (utils/deviceApps.js): nothing to start, no start command to ask for.
    state: device || isDeviceBuilders(builders) ? 'device' : serviceState(runner),
    spec: facts.spec,
    run: runs.kind,
    builder: runs.kind === 'code' ? builderOf({ spec: facts.spec, names: facts.names, stackLabel: info?.stackLabel || null }) : null,
    port,
    secretsMissing: config?.secretsMissing?.length || 0,
    failingChecks: blocked,
    endpoints: api.loading || api.error ? null : endpoints.length,
  });

  return (
    <div className="apps-infos" data-testid="apps-overview">
      <Card testId="apps-info-run" title="How it runs">
        <div className="apps-chips is-tight">
          {!facts.loading && <span className="apps-tag is-accent" data-testid="apps-run-kind">{builder || runs.label}</span>}
          {main && <span className="apps-tag" data-testid="apps-main" title="The service a shared preview link opens">Main service</span>}
        </div>
        {projectId && <DeploySwitch projectId={projectId} readOnly={readOnly} service={service} />}
        {!facts.loading && !builder && runs.detail && <p className="apps-run-detail" data-testid="apps-run-detail">{runs.detail}</p>}
        {lines.length > 0 ? (
          <dl className="apps-dl">{lines.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
        ) : <p className="apps-muted">{facts.loading ? 'Loading…' : 'Detected from the code: no .flow/service.json.'}</p>}
        {composeApp && (
          <EntryPicker
            entry={info?.composeEntry || null}
            onApply={onApplyEntry}
            onSave={readOnly ? null : saveEntry}
            readOnly={readOnly}
            running={isRunning(runner) && runner?.buildStatus !== 'building'}
            saved={specEntryOf(spec)}
          />
        )}
        {/* Custom command is always offered, also where vfs-service found no builder in the folder. */}
        {!facts.loading && builders && projectId && (
          <BuilderPicker
            docker={docker}
            info={builders}
            offerRestart={offerRestart}
            onRestart={onRestart}
            onSaved={onBuilderSaved}
            projectId={projectId}
            readOnly={readOnly}
            serviceName={`services/${service}`}
            spec={spec}
          />
        )}
      </Card>
      <ServiceAddresses info={info} />
      {runs.kind === 'compose' && runs.file && <ComposeCard file={runs.file} text={facts.files[runs.file] || null} />}
      {projectId && <DeviceBuildsCard projectId={projectId} readOnly={readOnly} service={service} />}
      <PerformanceCard runner={runner} />
      <SuggestionsCard actions={actions} items={suggestions} loading={facts.loading} />
      <TechnologiesCard list={technologies} loading={facts.loading} />
      <Card link="Open API tester" onLink={open('api')} testId="apps-info-endpoints" title="Endpoints">
        {api.error ? <p className="apps-muted">Not available.</p> : api.loading ? <p className="apps-muted">Loading…</p> : endpoints.length === 0 ? (
          <p className="apps-muted">No endpoints found for this service.</p>
        ) : (
          <ul className="apps-endpoints">
            {endpoints.slice(0, MAX_ENDPOINTS).map((endpoint) => (
              <li key={`${endpoint.method} ${endpoint.path}`}><strong className={`apps-method is-${String(endpoint.method).toLowerCase()}`}>{endpoint.method}</strong> {endpoint.path}</li>
            ))}
            {endpoints.length > MAX_ENDPOINTS && <li className="apps-muted">and {endpoints.length - MAX_ENDPOINTS} more</li>}
          </ul>
        )}
      </Card>
      <Card link="Open Config" onLink={open('config')} testId="apps-info-config" title="Config">
        {cfg.error ? <p className="apps-muted">Not available.</p> : cfg.loading ? <p className="apps-muted">Loading…</p> : !config ? (
          <p className="apps-muted">No variables, secrets or jobs yet.</p>
        ) : (
          <p>
            {plural(config.vars || 0, 'variable')} · {plural(config.secrets || 0, 'secret')} · {plural(config.jobs || 0, 'scheduled job')}
            {config.secretsMissing?.length > 0 && <> · <strong className="apps-warn">{config.secretsMissing.length} to set</strong></>}
          </p>
        )}
      </Card>
      <Card link="Open Quality" onLink={open('quality')} testId="apps-info-quality" title="Quality">
        {blocked > 0 ? <p><strong className="apps-bad">{plural(blocked, 'check')}</strong> block Publish across the project.</p>
          : checked ? <p>Nothing blocks Publish.</p>
            : <p className="apps-muted">{gateLoading ? 'Reading the checks…' : 'No check has run yet.'}</p>}
      </Card>
    </div>
  );
}

ServiceOverview.propTypes = {
  // The service's device app (utils/deviceApps.js deviceAppOf), when it is one: it never runs in the sandbox.
  device: PropTypes.object,
  // vfs-service's builders of this service (GET /runnables › builders["services/<name>"]): How it runs is their picker.
  builders: PropTypes.object,
  // The project's default app (short name; utils/defaultApp), for the "Main service" tag.
  defaultApp: PropTypes.string,
  info: PropTypes.object,
  // Whether Docker is on in the sandbox (null: not known).
  docker: PropTypes.bool,
  names: PropTypes.arrayOf(PropTypes.string),
  // "Restart to apply" a new preview entry point of a Docker Compose app while it runs (null: not offered).
  onApplyEntry: PropTypes.func,
  // Offer "Restart with this builder" (the service runs and its builder just changed).
  offerRestart: PropTypes.bool,
  // (nextSpec) => after the picker saved the service's .flow/service.json.
  onBuilderSaved: PropTypes.func,
  onOpen: PropTypes.func,
  onOpenLogs: PropTypes.func,
  onRestart: PropTypes.func,
  // (tab) => shows that tab in this page.
  onTab: PropTypes.func,
  projectId: PropTypes.string,
  readOnly: PropTypes.bool,
  runner: PropTypes.object,
  service: PropTypes.string.isRequired,
  shows: PropTypes.func,
};
